import produce from 'immer';

import {assert, assertAndContinue} from 'Asserts';
import {ContextMenuDisplayGroup, ContextMenuOption} from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import {StructuredTableToTableMetadata, Table} from 'notebook/tables/constants/TableConstants';
import {
  INSERT_CUSTOM_COLUMN_LEFT,
  INSERT_CUSTOM_COLUMN_RIGHT,
  INSERT_CUSTOM_SINGLE_INPUT_LEFT,
  INSERT_CUSTOM_SINGLE_INPUT_RIGHT,
} from 'notebook/tables/constants/TableContextMenuConstants';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';

import {CustomStructuredTableColumn, StructuredTable} from '../StructuredTableConstants';
import {logColumnAnalytics} from '../util/StructuredTableAnalyticsUtil';
import {getCustomColumnDataTypeDisplayName} from './CustomColumnConstants';
import getChangeCustomColumnTypeMenuOptions from './getChangeCustomColumnTypeMenuOptions';
import {runCreateCustomColumnConfigModal} from './runCustomColumnConfigModal';

type InsertColumnFn = (
  tableName: string,
  start: number,
  end: number,
  columnInfo: InsertableColumnInfo<CustomStructuredTableColumn> | {}
) => void;

/**
 * Define menu options for inserting custom columns.
 *
 * These menu options will be part of each table column's context menu.
 */
export default function getCustomColumnMenuOptions<TTableMetadata, TRowMetadata>(
  tableName: string,
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  structuredTableMetadata: StructuredTableToTableMetadata<TTableMetadata> | undefined,
  tableIsLocked: boolean,
  selectionRange: SelectionRange | null,
  insertColumnsLeft: InsertColumnFn,
  insertColumnsRight: InsertColumnFn,
  fileAnalyticsParams: FileAnalyticsParams,
  // the optional parameters below are necessary to allow for changing the column's data type
  table?: Table,
  updateColumnConstraint?: (columnIndex: number, columnConstraint: ColumnConstraint) => void
): Array<ContextMenuOption> {
  assertAndContinue(structuredTableMetadata != null, 'No table metadata provided.');
  const structuredTableAnalyticsParams = structuredTableMetadata
    ? {
        structuredTableKind: structuredTableMetadata.kind,
        structuredTableProvenanceUuid: structuredTableMetadata.provenanceUuid,
      }
    : null;

  const leftSelectedColumn =
    selectionRange?.minIndex != null ? structuredTable.columns[selectionRange.minIndex] : undefined;
  const rightSelectedColumn =
    selectionRange?.maxIndex != null ? structuredTable.columns[selectionRange.maxIndex] : undefined;
  const isLeftColumnSingleInput = Boolean(leftSelectedColumn?.isSingleInput);
  const isRightColumnSingleInput = Boolean(rightSelectedColumn?.isSingleInput);

  const createAndInsertColumn = async (
    insertColumnFunc: InsertColumnFn,
    options: {insertAsSingleInput: boolean}
  ): Promise<void> => {
    assert(selectionRange != null, 'Menu option should be disabled if no column is selected');
    let columnToInsert = await runCreateCustomColumnConfigModal();
    if (columnToInsert == null) {
      return;
    }
    const columnDefinition = columnToInsert.columnConstraints?.columnDefinition;
    assert(
      columnDefinition?.type === TableColumnType.CUSTOM,
      "Column to insert's type should be a custom column if insert custom column is clicked"
    );

    const customColumnDataType =
      columnToInsert.columnConstraints?.structuredTableColumn?.customColumnConfig.dataType;
    assert(customColumnDataType != null, 'Missing custom column data type.');

    // If the inserting position is a single input, the new column should also be a single input.
    if (options.insertAsSingleInput) {
      columnToInsert = produce(columnToInsert, (draft) => {
        // !s are okay because the above assert ensures this check exists
        draft.columnConstraints!.structuredTableColumn!.isSingleInput = true;
      });
    }

    logColumnAnalytics({
      analyticsEvent: ELN_ANALYTICS_ACTIONS.INSERTED_CUSTOM_COLUMN,
      fileAnalyticsParams,
      structuredTableAnalyticsParams,
      additionalMetadata: {
        customColumnProvenanceUuid: columnToInsert.columnConstraints?.structuredTableColumn?.provenanceUuid,
        customColumnName: columnToInsert.columnConstraints?.structuredTableColumn?.name,
        customColumnDataType: getCustomColumnDataTypeDisplayName(customColumnDataType),
      },
    });
    insertColumnFunc(tableName, selectionRange.maxIndex, selectionRange.maxIndex, columnToInsert);
  };
  const changeTypeMenuOptions =
    table && updateColumnConstraint
      ? getChangeCustomColumnTypeMenuOptions<TTableMetadata>(
          table,
          tableIsLocked,
          selectionRange,
          updateColumnConstraint,
          fileAnalyticsParams
        )
      : [];

  return [
    ...changeTypeMenuOptions,
    {
      name: isLeftColumnSingleInput ? INSERT_CUSTOM_SINGLE_INPUT_LEFT : INSERT_CUSTOM_COLUMN_LEFT,
      hide: () => tableIsLocked,
      click: async (): Promise<void> => {
        await createAndInsertColumn(insertColumnsLeft, {insertAsSingleInput: isLeftColumnSingleInput});
      },
      displayGroup: ContextMenuDisplayGroup.BOTTOM,
      dataTestAttrs: {component: 'ContextMenu', element: 'insertCustomColumn', key: 'left'},
    },
    {
      name: isRightColumnSingleInput ? INSERT_CUSTOM_SINGLE_INPUT_RIGHT : INSERT_CUSTOM_COLUMN_RIGHT,
      hide: () => tableIsLocked,
      click: async (): Promise<void> => {
        await createAndInsertColumn(insertColumnsRight, {insertAsSingleInput: isRightColumnSingleInput});
      },
      displayGroup: ContextMenuDisplayGroup.BOTTOM,
      dataTestAttrs: {component: 'ContextMenu', element: 'insertCustomColumn', key: 'right'},
    },
  ];
}
